export type RulesChunk = {
  id: string; // e.g. "702.1"
  text: string;
};

export class RulesClient {
  private chunks: RulesChunk[] = [];

  constructor() {}

  /**
   * Parse the full rules text into searchable chunks.
   * This is a simplified parser assuming the rules text format.
   * MTG rules often have sections like "100. General", "101. ...".
   * We will split by newlines that start with a number.
   */
  parseRules(text: string) {
    // Normalize line endings to support CR, CRLF, LF
    let normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // HEURISTIC: If the text has very few lines but is long, it's likely minified.
    // The official rules text is usually massive (~1MB+).
    // If we have < 100 lines for > 1000 chars, we should try to expand it.
    if (normalizedText.split('\n').length < 100 && normalizedText.length > 1000) {
        console.log("Detected minified rules text. Attempting to expand...");
        // This regex looks for a period or space, followed by a Rule ID (e.g. 100.1, 100.1a), followed by a space.
        // We replace it with a newline before the Rule ID.
        // Capture groups:
        // $1: Preceding character (start of line, period, or space)
        // $2: The Rule ID
        const expandRegex = /(^|[\.\s])(\d{1,3}(?:\.\d+[a-z]?)?\.?)\s/g;
        normalizedText = normalizedText.replace(expandRegex, "$1\n$2 ");
    }

    const lines = normalizedText.split('\n');
    const chunks: RulesChunk[] = [];
    let currentId = "Intro";
    let currentText = "";

    // Regex to match rule headings like "100.1.", "100.1a", "1. Game Concepts"
    // Now simplified to look for lines starting with digits
    const ruleStartRegex = /^(\d{1,3}(?:\.\d+[a-z]?)?\.?)\s+(.+)/;

    for (const line of lines) {
      const match = line.match(ruleStartRegex);
      if (match) {
        // Save previous chunk if it has content
        if (currentText.trim()) {
           chunks.push({ id: currentId!, text: currentText.trim() });
        }
        // Start new chunk
        currentId = match[1] || "Unknown";
        currentText = line;
      } else {
        currentText += "\n" + line;
      }
    }
    // Add last chunk
    if (currentText.trim()) {
        chunks.push({ id: currentId, text: currentText.trim() });
    }

    this.chunks = chunks;
    console.log(`Parsed ${this.chunks.length} rule chunks.`);
  }

  /**
   * Find rules relevant to the query using simple keyword matching.
   * Ranks by number of unique keywords found in the chunk.
   */
  findRelevantRules(query: string, limit: number = 3): string {
    if (!this.chunks.length) return "";

    const keywords = query.toLowerCase().split(/\s+/).filter(w => w.length > 3); // Ignore short words
    if (keywords.length === 0) return "";

    const scoredChunks = this.chunks.map(chunk => {
      const textLower = chunk.text.toLowerCase();
      let score = 0;
      for (const word of keywords) {
        if (textLower.includes(word)) {
          score++;
        }
      }
      return { chunk, score };
    });

    // innovative "RAG": filter for score > 0, sort by score desc
    const topChunks = scoredChunks
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score);

    if (topChunks.length === 0) return "";

    // Accumulate chunks until limit or max chars reached
    const selectedChunks = [];
    let currentLength = 0;
    const MAX_CHARS = 3000; // Hard limit for context size per search

    for (const item of topChunks) {
        if (selectedChunks.length >= limit) break;
        if (currentLength + item.chunk.text.length > MAX_CHARS) {
            // If even the first chunk is too big, maybe truncate it? 
            // For now, just skip adding more if we're essentially full.
            if (selectedChunks.length === 0) {
                 // Include at least one, simplified.
                 selectedChunks.push(item);
            }
            break;
        }
        selectedChunks.push(item);
        currentLength += item.chunk.text.length;
    }

    return selectedChunks.map(item => `[Rule ${item.chunk.id}] ${item.chunk.text}`).join("\n\n");
  }

  getChunkCount() {
    return this.chunks.length;
  }
}
